import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { Layout } from 'antd';
import { useEffect } from 'react';
import {
	currentUser as queryCurrentUser,
	storeExternalUser,
} from '@/services/auth';
import defaultSettings from '../config/defaultSettings';
import Navbar from './components/NavBar';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
import { ConfigProvider } from 'antd';
import enUS from 'antd/locale/en_US';
import BackendUnavailableModal from '@/components/Modals/BackendUnavailableModal';
import InactivityWarningModal from '@/components/Modals/InactivityWarningModal';
import { ProjectProvider } from '@/context/ProjectContext';
import { useBackendHealthMonitor } from '@/hooks/useBackendHealthMonitor';
import { useInactivityMonitor } from '@/hooks/useInactivityMonitor';
import { InteractionManager } from '@/SDK/InteractionManager';
import { getLayoutColors, getThemeConfig } from '@/theme/SpeecargoTheme';
import {
	destroyActivityTracker,
	initActivityTracker,
} from '@/utils/activityTracker';
import { authLogout, releaseWorkspaceIfOpen } from '@/utils/authUtils';
import { LOGIN_PATH } from '@/utils/contants';
import {
	getOktaUser,
	getValidOktaAccessToken,
	isOktaAuthenticated,
} from '@/utils/oktaAuth';
import runtimeConfig from '@/utils/runtimeConfig';

const isDev = runtimeConfig.isDev;
const isDevelopmentEnabled = runtimeConfig.isDevelopmentEnabled;

/**
 * Initial state loader
 */
export async function getInitialState(): Promise<{
	settings?: Partial<LayoutSettings>;
	currentUser?: API.CurrentUser;
	loading?: boolean;
	fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
	const fetchUserInfo = async () => {
		try {
			const msg = await queryCurrentUser({ skipErrorHandler: true });
			return msg.data;
		} catch (_error) {
			history.push(LOGIN_PATH);
		}
		return undefined;
	};

	const fetchOktaUserInfo = async (): Promise<API.CurrentUser | undefined> => {
		try {
			const authenticated = await isOktaAuthenticated();
			if (!authenticated) {
				console.log('[Auth] Okta: not authenticated (no valid tokens found)');
				return undefined;
			}

			// Proactively refresh token if close to expiry before proceeding
			await getValidOktaAccessToken();

			const oktaUser = await getOktaUser();
			// Transform Okta user to application user format
			const user: API.CurrentUser = {
				name:
					(typeof oktaUser.name === 'string' ? oktaUser.name : null) ||
					(typeof oktaUser.preferred_username === 'string'
						? oktaUser.preferred_username
						: null) ||
					'User',
				avatar:
					typeof oktaUser.picture === 'string' ? oktaUser.picture : undefined,
				email: typeof oktaUser.email === 'string' ? oktaUser.email : undefined,
				userid: oktaUser.sub || '',
				access: 'admin', // You may want to determine this from Okta groups/claims
			};

			// Store user in localStorage
			storeExternalUser(user);

			return user;
		} catch (error) {
			console.error('[Auth] Error fetching Okta user:', error);
			return undefined;
		}
	};

	const { location } = history;
	// Normalize pathname by removing trailing slash for consistent matching
	// (nginx may 301-redirect adding a trailing slash, e.g. /login/callback → /login/callback/)
	const pathname =
		location.pathname.endsWith('/') && location.pathname !== '/'
			? location.pathname.slice(0, -1)
			: location.pathname;
	if (![LOGIN_PATH, '/login/callback'].includes(pathname)) {
		// First check if user is authenticated via Okta
		let currentUser = await fetchOktaUserInfo();

		// If not authenticated via Okta, try backend authentication
		if (!currentUser) {
			currentUser = await fetchUserInfo();
		}

		return {
			fetchUserInfo,
			currentUser,
			settings: defaultSettings as Partial<LayoutSettings>,
		};
	}
	return {
		fetchUserInfo,
		settings: defaultSettings as Partial<LayoutSettings>,
	};
}

/**
 * ProLayout config with Navbar included
 */
export const layout: RunTimeLayoutConfig = ({
	initialState,
	setInitialState,
}) => {
	const handleLogout = async () => {
		await authLogout(setInitialState);
		history.push(LOGIN_PATH);
	};

	const themeConfig = getThemeConfig('speedcargo'); // Using Speedcargo theme by default
	const layoutColors = getLayoutColors('speedcargo');

	// const themeConfig = getThemeConfig('twa'); // Using Speedcargo theme by default
	// const layoutColors = getLayoutColors('twa');

	useEffect(() => {
		initActivityTracker();
		let loggingOut = false;

		const handleTokenRenewed = () => {
			console.log('Okta token renewed → refreshing workspace locks');

			const match = window.location.pathname.match(
				/^\/workspaceDetails\/(\d+)/,
			);
			if (!match) return;

			InteractionManager.getInstance()
				.API.workspaceLockRefresh()
				.catch(() => {
					console.warn('Workspace lock refresh failed → logging out');
					window.dispatchEvent(new CustomEvent('okta-token-expired'));
				});
		};

		const handleTokenExpired = () => {
			if (loggingOut) return;
			loggingOut = true;
			console.warn('Okta token expired → logging out');

			localStorage.removeItem('cargomind_current_user');
			localStorage.removeItem('cargomind_session');
			setInitialState((s) => ({ ...s, currentUser: undefined }));

			// Navigate immediately. Don't await logoutFromOkta() because
			// oktaAuth.signOut() can hang when Okta is unreachable, which
			// freezes the page with "This page isn't responding".
			// NOTE: Do NOT call releaseWorkspaceIfOpen() here — the token is
			// already expired so the API call would fail and re-dispatch
			// okta-token-expired, causing an infinite loop.
			window.location.href = LOGIN_PATH;
		};

		const handleInactivityLogout = async () => {
			if (loggingOut) return;
			loggingOut = true;
			console.warn('Inactivity timeout → releasing workspace and logging out');

			// Token is still valid in the inactivity case, so try to release the workspace
			await releaseWorkspaceIfOpen();

			localStorage.removeItem('cargomind_current_user');
			localStorage.removeItem('cargomind_session');
			setInitialState((s) => ({ ...s, currentUser: undefined }));

			window.location.href = LOGIN_PATH;
		};

		window.addEventListener('okta-token-renewed', handleTokenRenewed);
		window.addEventListener('okta-token-expired', handleTokenExpired);
		window.addEventListener('inactivity-logout', handleInactivityLogout);

		return () => {
			destroyActivityTracker();
			window.removeEventListener('okta-token-renewed', handleTokenRenewed);
			window.removeEventListener('okta-token-expired', handleTokenExpired);
			window.removeEventListener('inactivity-logout', handleInactivityLogout);
		};
	}, []);

	const {
		isDown: backendDown,
		isChecking: backendChecking,
		checkNow: checkBackend,
	} = useBackendHealthMonitor();

	const { warningOpen, secondsRemaining, handleStaySignedIn, handleSignOut } =
		useInactivityMonitor();

	return {
		fixedHeader: true,
		pure: true, // disable default header from ProLayout

		childrenRender: (children) => (
			<ConfigProvider componentSize="small" theme={themeConfig} locale={enUS}>
				<Layout
					style={{
						minHeight: layoutColors.layout.minHeight,
						backgroundColor: layoutColors.layout.background,
						overflow: 'hidden',
					}}
				>
					<ProjectProvider>
						<Navbar
							currentUser={initialState?.currentUser}
							onLogout={handleLogout}
						/>
						<Layout.Content
							style={{
								marginTop: layoutColors.content.marginTop,
								paddingTop: layoutColors.content.paddingVertical,
								paddingBottom: layoutColors.content.paddingVertical,
								backgroundColor: layoutColors.content.background,
								overflow: 'hidden',
								display: 'grid', // ensure that the main is as tall as the layout
							}}
						>
							{children}
						</Layout.Content>
					</ProjectProvider>
					<InactivityWarningModal
						open={warningOpen}
						secondsRemaining={secondsRemaining}
						onStaySignedIn={handleStaySignedIn}
						onSignOut={handleSignOut}
					/>
					<BackendUnavailableModal
						open={backendDown}
						isChecking={backendChecking}
						onRefresh={checkBackend}
					/>
				</Layout>
			</ConfigProvider>
		),
	};
};

/**
 * Unified API request config
 */
export const request: RequestConfig = {
	baseURL: isDev ? '' : 'https://proapi.azurewebsites.net',
	...errorConfig,
	requestInterceptors: [
		...(errorConfig.requestInterceptors || []),
		(config: any) => {
			// Block API requests if DEVELOPMENT flag is not enabled
			if (!isDevelopmentEnabled) {
				console.warn(
					'API services are disabled. Set DEVELOPMENT=true in .env to enable.',
				);
				// Return a rejected promise to prevent the request
				return Promise.reject(
					new Error(
						'API services are disabled in production mode. Set DEVELOPMENT=true in .env to enable.',
					),
				);
			}
			return config;
		},
	],
};
